import React, { useState } from 'react';
import { apiFetch } from '../api';
import HelperIcon from './HelperIcon';

// Grade level options
const GRADE_OPTIONS = [
  'Pre-K', 'Kindergarten',
  '1st Grade', '2nd Grade', '3rd Grade', '4th Grade', '5th Grade',
  '6th Grade', '7th Grade', '8th Grade',
  '9th Grade', '10th Grade', '11th Grade', '12th Grade',
  'College Freshman', 'College Sophomore', 'College Junior', 'College Senior',
  'Graduate Student', 'Adult Learner'
];

// Subject options
const SUBJECT_OPTIONS = [
  'Math - Elementary',
  'Math - Middle School',
  'Math - High School',
  'Algebra',
  'Geometry',
  'Pre-Calculus',
  'Calculus',
  'Statistics',
  'Reading',
  'Writing',
  'English/Language Arts',
  'Literature',
  'Science - General',
  'Biology',
  'Chemistry',
  'Physics',
  'Social Studies',
  'History',
  'Geography',
  'Test Prep - SAT',
  'Test Prep - ACT',
  'Computer Science',
  'Spanish',
  'French',
  'Study Skills'
];

// Adorable demo student profiles
const DEMO_STUDENTS = [
  {
    display_name: 'Luna Learner',
    grade_year_level: '5th Grade',
    target_subjects: ['Science - General', 'Math - Elementary', 'Reading'],
    personality: 'Loves space exploration and dinosaurs!'
  },
  {
    display_name: 'Max Mathlete',
    grade_year_level: '7th Grade',
    target_subjects: ['Algebra', 'Geometry', 'Test Prep - SAT'],
    personality: 'Dreams of being a video game designer'
  },
  {
    display_name: 'Sophia Scholar',
    grade_year_level: '10th Grade',
    target_subjects: ['Biology', 'Chemistry', 'Writing'],
    personality: 'Aspiring marine biologist, loves sea turtles'
  },
  {
    display_name: 'Tyler Thinker',
    grade_year_level: '3rd Grade',
    target_subjects: ['Reading', 'Math - Elementary', 'Study Skills'],
    personality: 'Building robots and reading comic books'
  },
  {
    display_name: 'Aria Artist',
    grade_year_level: '8th Grade',
    target_subjects: ['English/Language Arts', 'Literature', 'History'],
    personality: 'Creative writer working on her first novel'
  }
];

const AddStudentPanel = ({ onClose, onStudentCreated, isAdmin, assignToTutorId = null }) => {
  const [formData, setFormData] = useState({
    student_email: '',
    display_name: '',
    grade_year_level: '',
    target_subjects: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [isDemoStudent, setIsDemoStudent] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubjectToggle = (subject) => {
    setFormData(prev => {
      const subjects = prev.target_subjects.includes(subject)
        ? prev.target_subjects.filter(s => s !== subject)
        : [...prev.target_subjects, subject];
      return { ...prev, target_subjects: subjects };
    });
    if (errors.target_subjects) {
      setErrors(prev => ({ ...prev, target_subjects: null }));
    }
  };

  const handleDemoFill = () => {
    const randomDemo = DEMO_STUDENTS[Math.floor(Math.random() * DEMO_STUDENTS.length)];
    setFormData({
      student_email: '',
      display_name: randomDemo.display_name,
      grade_year_level: randomDemo.grade_year_level,
      target_subjects: [...randomDemo.target_subjects]
    });
    setIsDemoStudent(true);
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};

    if (!isDemoStudent) {
      const emailVal = (formData.student_email || '').trim();
      if (!emailVal) {
        newErrors.student_email = 'Student email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) {
        newErrors.student_email = 'Please enter a valid email address';
      }
    }

    if (!formData.grade_year_level) {
      newErrors.grade_year_level = 'Please select a grade level';
    }

    if (formData.target_subjects.length === 0) {
      newErrors.target_subjects = 'Please select at least one subject';
    }

    if (formData.display_name && formData.display_name.length > 100) {
      newErrors.display_name = 'Display name must be 100 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const requestBody = {
        student_email: formData.student_email.trim().toLowerCase() || undefined,
        display_name: formData.display_name || undefined,
        grade_year_level: formData.grade_year_level,
        target_subjects: formData.target_subjects.join(', '),
        is_demo_student: isDemoStudent
      };

      // Auto-assign to tutor if provided (for team captains)
      if (assignToTutorId) {
        requestBody.assign_to_tutor_id = assignToTutorId;
      }

      const response = await apiFetch('/api/students', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create student');
      }

      const result = await response.json();
      onStudentCreated(result.student);

    } catch (error) {
      console.error('Error creating student:', error);
      alert(error.message || 'Failed to create student. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="add-student-panel-overlay" onClick={handleOverlayClick}>
      <div className="add-student-panel">
        <header className="add-student-panel-header">
          <h2>Add New Student</h2>
          <HelperIcon
            title="Adding a Student"
            content="Create a student profile to begin the matching and tutoring workflow. Required: grade level and at least one subject. The student will appear in the matching pool after creation."
            size="small"
          />
          <button
            className="close-btn"
            onClick={onClose}
            aria-label="Close panel"
          >
            &times;
          </button>
        </header>

        <form className="add-student-form" onSubmit={handleSubmit}>
          {/* Admin-only Demo Student Button */}
          {isAdmin && (
            <button
              type="button"
              className="demo-student-button"
              onClick={handleDemoFill}
            >
              Add Demo Student
            </button>
          )}

          {/* Student Email (Required) */}
          <div className="form-group">
            <label htmlFor="student_email">
              Student Email <span className="required">*</span>
            </label>
            <p className="form-hint">The Gmail address the student will use to log in</p>
            <input
              type="email"
              id="student_email"
              name="student_email"
              className={`form-input ${errors.student_email ? 'error' : ''}`}
              value={formData.student_email}
              onChange={handleChange}
              placeholder="student@gmail.com"
              autoComplete="email"
            />
            {errors.student_email && (
              <span className="form-error">{errors.student_email}</span>
            )}
          </div>

          {/* Display Name (Optional) */}
          <div className="form-group">
            <label htmlFor="display_name">
              Display Name <span className="optional">(optional)</span>
            </label>
            <input
              type="text"
              id="display_name"
              name="display_name"
              className={`form-input ${errors.display_name ? 'error' : ''}`}
              value={formData.display_name}
              onChange={handleChange}
              placeholder="e.g., Luna Learner"
              maxLength={100}
            />
            {errors.display_name && (
              <span className="form-error">{errors.display_name}</span>
            )}
          </div>

          {/* Grade Level (Required) */}
          <div className="form-group">
            <label htmlFor="grade_year_level">
              Grade Level <span className="required">*</span>
            </label>
            <select
              id="grade_year_level"
              name="grade_year_level"
              className={`form-select ${errors.grade_year_level ? 'error' : ''}`}
              value={formData.grade_year_level}
              onChange={handleChange}
            >
              <option value="">Select a grade level...</option>
              {GRADE_OPTIONS.map(grade => (
                <option key={grade} value={grade}>{grade}</option>
              ))}
            </select>
            {errors.grade_year_level && (
              <span className="form-error">{errors.grade_year_level}</span>
            )}
          </div>

          {/* Target Subjects (Required) */}
          <div className="form-group">
            <label>
              Target Subjects <span className="required">*</span>
            </label>
            <p className="form-hint">Select all subjects the student needs help with</p>
            <div className={`subject-grid ${errors.target_subjects ? 'error' : ''}`}>
              {SUBJECT_OPTIONS.map(subject => (
                <button
                  key={subject}
                  type="button"
                  className={`subject-chip ${formData.target_subjects.includes(subject) ? 'selected' : ''}`}
                  onClick={() => handleSubjectToggle(subject)}
                >
                  {subject}
                </button>
              ))}
            </div>
            {errors.target_subjects && (
              <span className="form-error">{errors.target_subjects}</span>
            )}
            {formData.target_subjects.length > 0 && (
              <div className="selected-subjects">
                <strong>Selected:</strong> {formData.target_subjects.join(', ')}
              </div>
            )}
          </div>
        </form>

        <div className="add-student-actions">
          <button
            type="button"
            className="btn-cancel"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-create"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creating...' : 'Create Student'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddStudentPanel;
